// Runtime implementation of the feature-flag registry.
// Resolution order (highest wins):
//   1. env[enableEnvVar]  → force ON
//   2. ctx.workspaceFlags → per-workspace override
//   3. env[disableEnvVar] → force OFF
//   4. flag.defaultEnabled
// Unknown flags resolve to `false`.

import type {
  FlagDefinition,
  FlagId,
  FlagRegistryOptions,
  FlagResolutionContext,
  FlagsRegistry,
} from './types.js';

const DEFAULT_ENABLE_ENV = 'FEATURE_FLAGS_ENABLED';
const DEFAULT_DISABLE_ENV = 'FEATURE_FLAGS_DISABLED';

function readProcessEnv(name: string): string | undefined {
  const proc =
    typeof globalThis !== 'undefined'
      ? (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
      : undefined;
  return proc?.env?.[name];
}

function parseEnvList(raw: string | undefined): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0),
  );
}

export function createFlagsRegistry(opts: FlagRegistryOptions): FlagsRegistry {
  const flags = opts.flags;
  const enableEnvVar = opts.enableEnvVar ?? DEFAULT_ENABLE_ENV;
  const disableEnvVar = opts.disableEnvVar ?? DEFAULT_DISABLE_ENV;

  return {
    isEnabled(flagId: FlagId, ctx: FlagResolutionContext): boolean {
      // Keep env lookup lazy so tests can mutate process.env between calls.
      const enabledEnv = parseEnvList(readProcessEnv(enableEnvVar));
      if (enabledEnv.has(flagId)) return true;

      if (ctx.workspaceFlags && Object.prototype.hasOwnProperty.call(ctx.workspaceFlags, flagId)) {
        const override = ctx.workspaceFlags[flagId];
        if (typeof override === 'boolean') return override;
      }

      const disabledEnv = parseEnvList(readProcessEnv(disableEnvVar));
      if (disabledEnv.has(flagId)) return false;

      const def = flags[flagId];
      if (!def) return false;
      return def.defaultEnabled;
    },

    list() {
      return Object.entries(flags).map(([flagId, def]: [string, FlagDefinition]) => {
        const entry: { flagId: FlagId; defaultEnabled: boolean; description?: string } = {
          flagId,
          defaultEnabled: def.defaultEnabled,
        };
        if (def.description !== undefined) {
          entry.description = def.description;
        }
        return entry;
      });
    },
  };
}

/**
 * Shipped defaults. Apps SHOULD spread this into their own registry so they
 * inherit canonical flags but can add their own:
 *
 *   const registry = createFlagsRegistry({
 *     flags: { ...DEFAULT_FLAGS, 'crm.new-deal-form': { defaultEnabled: false } },
 *   });
 */
export const DEFAULT_FLAGS: Record<FlagId, { defaultEnabled: boolean; description: string }> = {
  'pro-mode': {
    defaultEnabled: false,
    description: 'Enable Pro-mode advanced UI',
  },
  'ai-chat': {
    defaultEnabled: true,
    description: 'Show AI chat panels',
  },
  'enrich-contact-experimental': {
    defaultEnabled: false,
    description: 'Experimental enrich_contact AI tool',
  },
  'scenario-engine-visual-editor': {
    defaultEnabled: false,
    description: '[PHASE-2] xyflow scenario editor',
  },
};
